import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ROUTES } from "../../configs/routes";
import { useCookie } from "../../hooks/cookie-hooks";
import { ApplicationState } from "../../store";
import { UserState } from "../../store/reducers/user-reducer";
import Alert from "../__common/Alert/Alert";
import { Typography } from "../__common/Typography/Typography";
import { Button } from "../__common/_controls";
import { Trans } from "react-i18next";
import { useTranslation } from "react-i18next";
import "./CookieModal.scss";
import { ManageCookieModal } from "./ManageCookie";
import {
	EVENT_COOKIE_NOTICE_ACTION_ACCEPT_ALL,
	EVENT_COOKIE_NOTICE_ACTION_CONTINUE_NO_ACCEPT,
	EVENT_COOKIE_NOTICE_ACTION_HAPPY_COOKIES,
	EVENT_ACTION_MANAGE_COOKIES,
} from "../../constants/analytics";
import { GA4EventName } from "../../types/analytics";
import { UtilsKeyboardAccessibility } from "../../utils/UtilsKeyboardAccessibility";
import FocusTrapping from "../__hoc/FocusTrapping";
import GoogleAnalyticsV2 from "../../utils/GoogleAnalyticsV2";

export const NonEuCookieAlert: React.FC = () => {
	const { isCookieNoticeOpen, setCookiePreference, setCookieNoticeModalOpen } =
		useCookie();
	const [isNonEuShow, setNonEuShow] = useState(isCookieNoticeOpen);
	const userState = useSelector<ApplicationState, UserState>(
		(state) => state.user
	);
	const { t } = useTranslation();

	useEffect(() => {
		if (!userState.userRegion.isFromEurope) {
			setNonEuShow(isCookieNoticeOpen);
		}
	}, [isCookieNoticeOpen, userState.userRegion.isFromEurope]);

	useEffect(() => {
		if (isNonEuShow) {
			UtilsKeyboardAccessibility.setFocus("cookie-noneu");
		}
	}, [isNonEuShow]);

	useEffect(() => {
		UtilsKeyboardAccessibility.addTabEventListener(
			"cookie-noneu-title",
			"pseudo-noneu"
		);
		UtilsKeyboardAccessibility.addTabEventListener(
			"cookie-accept-button",
			"cookie-noneu"
		);

		return () => {
			UtilsKeyboardAccessibility.removeTabEventListener(
				"cookie-noneu-title",
				"pseudo-noneu"
			);
			UtilsKeyboardAccessibility.removeTabEventListener(
				"cookie-accept-button",
				"cookie-noneu"
			);
		};
	}, []);

	return (
		<FocusTrapping
			isActive={isNonEuShow}
			onHandleClose={() => setNonEuShow(false)}
			modalId="cookie-noneu"
		>
			<div className="cookie-noneu" id="cookie-noneu">
				<Alert
					show={isNonEuShow}
					title={t("PRIVACY_COOKIE_NOTICE_TITLE")}
					ttsButtonId="cookie-noneu-title"
				>
					<div className="cookie-noneu__text">
						<Typography variant="body2" className="subText">
							<Trans
								i18nKey="PRIVACY_COOKIE_NOTICE_DEFAULT"
								components={{
									a: (
										<Link
											data-testid="cookie-noneu-text-link"
											to={ROUTES.PRIVACY_POLICY}
											className="cookie-noneu__text__link"
											onClick={() => {
												setNonEuShow(false);
												setCookieNoticeModalOpen(false);
											}}
										/>
									),
								}}
							/>
						</Typography>
					</div>
					<Alert.Footer>
						<Button
							title={t("PRIVACY_COOKIE_NOTICE_DEFAULT_BTN")}
							className="cookie-noneu__accept-button"
							onClick={() => {
								GoogleAnalyticsV2.trackEvent(GA4EventName.CookieConsent, {
									action: EVENT_COOKIE_NOTICE_ACTION_ACCEPT_ALL,
								});
								setCookiePreference(true, true);
								document.getElementById("header")?.focus();
							}}
							testId="cookie-accept-button"
							id="cookie-accept-button"
						/>
					</Alert.Footer>
				</Alert>
			</div>
		</FocusTrapping>
	);
};

export const EuCookieAlert: React.FC = () => {
	const {
		isCookieNoticeOpen,
		isManageCookieOpen,
		setCookiePreference,
		setManageCookieOpen,
		setCookieNoticeOpen,
		setCookieNoticeModalOpen,
	} = useCookie();
	const [isShow, setEuShow] = React.useState(isCookieNoticeOpen);
	const userState = useSelector<ApplicationState, UserState>(
		(state) => state.user
	);
	const { t } = useTranslation();

	useEffect(() => {
		if (userState.userRegion.isFromEurope) {
			setEuShow(isCookieNoticeOpen);
		}
	}, [isCookieNoticeOpen, userState.userRegion.isFromEurope]);

	useEffect(() => {
		if (isShow) {
			UtilsKeyboardAccessibility.setFocus("cookie-eu");
		}
	}, [isShow]);

	useEffect(() => {
		UtilsKeyboardAccessibility.addTabEventListener(
			"accept-cookies-button",
			"cookie-eu"
		);

		return () => {
			UtilsKeyboardAccessibility.removeTabEventListener(
				"accept-cookies-button",
				"cookie-eu"
			);
		};
	}, []);

	return (
		<>
			<FocusTrapping
				onHandleClose={() => setEuShow(false)}
				isActive={isShow}
				modalId="cookie-eu"
			>
				<div className="cookie-eu" id="cookie-eu">
					<Alert
						show={isShow}
						title={t("PRIVACY_COOKIE_NOTICE_TITLE")}
						// noninteractive fragment suppresses Alert's default header close
						// button; decline is offered once, as a footer action
						customClose={<></>}
					>
						<div className="cookie-eu__text">
							<Typography variant="body2" className="subText">
								<Trans
									i18nKey="PRIVACY_COOKIE_NOTICE_EU_NEW"
									components={{
										a1: (
											<Link
												data-testid="a1-button"
												to={ROUTES.PRIVACY_POLICY}
												className="cookie-noneu__text__link"
												onClick={() => {
													setEuShow(false);
													setCookieNoticeModalOpen(false);
												}}
											/>
										),
										a2: (
											<Link
												data-testid="a2-button"
												to={ROUTES.COOKIE_POLICY}
												className="cookie-noneu__text__link"
												onClick={() => {
													setEuShow(false);
													setCookieNoticeModalOpen(false);
												}}
											/>
										),
									}}
								/>
							</Typography>
						</div>
						<Alert.Footer>
							<div className="button-container">
								<Button
									type="secondary"
									testId="close-modal-btn"
									title={t("PRIVACY_COOKIE_NOTICE_EU_BTN_CONFIGURE")}
									onClick={() => {
										GoogleAnalyticsV2.trackEvent(GA4EventName.CookieManage, {
											action: EVENT_ACTION_MANAGE_COOKIES,
										});
										setManageCookieOpen(true);
										setCookieNoticeOpen(false);
										setCookieNoticeModalOpen(false);
									}}
								></Button>
							</div>
							<div className="button-container">
								<Button
									testId="cookie-notice-eu-btn"
									title={t("PRIVACY_COOKIE_NOTICE_EU_BTN_ACCEPTALL")}
									onClick={() => {
										setCookiePreference(true, true);
										GoogleAnalyticsV2.trackEvent(GA4EventName.CookieConsent, {
											action: EVENT_COOKIE_NOTICE_ACTION_HAPPY_COOKIES,
										});
									}}
									id="accept-cookies-button"
								></Button>
							</div>
							<div className="button-container">
								<Button
									testId="close-btn"
									title={t(
										"PRIVACY_COOKIE_NOTICE_EU_BTN_DECLINEALL"
									)}
									onClick={() => {
										GoogleAnalyticsV2.trackEvent(GA4EventName.CookieConsent, {
											action: EVENT_COOKIE_NOTICE_ACTION_CONTINUE_NO_ACCEPT,
										});
										setCookiePreference(false, false);
									}}
								></Button>
							</div>
						</Alert.Footer>
					</Alert>
				</div>
			</FocusTrapping>
			<FocusTrapping
				isActive={isManageCookieOpen}
				onHandleClose={() => setManageCookieOpen(false)}
				modalId="manage-cookie2"
			>
				<ManageCookieModal isOpen={isManageCookieOpen} id="manage-cookie2" />
			</FocusTrapping>
		</>
	);
};

export * from "./ManageCookie";
